<ham-modal
    [title]="title"
    [saveText]="saveLabel"
    [showCancelButton]="true"
    [cancelText]="cancelLabel"
    size="xl"
    (save)="onSave()"
    (cancel)="onCancel()"
    [disabled]="isSaveDisabled"
>
    <div class="fx-hist-body">
        <div class="fx-hist-subtitle">
            <strong>{{ request.accountLabel }}</strong>
        </div>

        <!-- The whole chain in one block: prior-year balance and rate produce the opening, the
             movements below explain the change, and the closing yields the weighted rate. -->
        <div class="fx-hist-summary">
            <div class="fx-hist-summary-head">
                <span></span>
                <span class="num">{{ functionalLabel }} ({{ functionalCurrencyName }})</span>
                <span class="num">{{ rateLabel }}</span>
                <span class="num">{{ displayLabel }} ({{ displayCurrencyName }})</span>
            </div>

            <div class="fx-hist-summary-row">
                <span>{{ openingLabel }}</span>
                <span class="num">{{ request.functionalOpening | number : '1.2-2' }}</span>
                <span class="num">
                    <!-- Editable only when the prior year is historical; otherwise it is the
                         period rate and belongs to step 1. -->
                    <ng-container *ngIf="isPreviousYearEditable; else previousRateStatic">
                        <input
                            hamInput
                            type="number"
                            step="0.00001"
                            [decimalPlaces]="rateDecimalPlaces"
                            [ngModel]="previousYearRate"
                            [ngModelOptions]="{ standalone: true }"
                            (ngModelChange)="onPreviousYearRateChange($event)"
                        />
                    </ng-container>
                    <ng-template #previousRateStatic>
                        {{ previousYearRate == null ? '—' : (previousYearRate | number : '1.5-5') }}
                    </ng-template>
                </span>
                <span class="num">
                    <ng-container *ngIf="isPreviousYearEditable; else previousDisplayStatic">
                        <input
                            hamInput
                            type="number"
                            step="0.01"
                            [ngModel]="previousYearDisplay"
                            [ngModelOptions]="{ standalone: true }"
                            (ngModelChange)="onPreviousYearDisplayChange($event)"
                            (blur)="onPreviousYearDisplayBlur()"
                        />
                    </ng-container>
                    <ng-template #previousDisplayStatic>
                        {{ previousYearDisplay == null ? '—' : (previousYearDisplay | number : '1.2-2') }}
                    </ng-template>
                </span>
            </div>

            <div class="fx-hist-summary-row" *ngIf="!isPreviousYear">
                <span>{{ closingLabel }}</span>
                <span class="num">{{ request.functionalClosing | number : '1.2-2' }}</span>
                <span class="num">
                    {{ weightedRate == null ? '—' : (weightedRate | number : '1.5-5') }}
                    <em class="fx-hist-weighted" *ngIf="weightedRate != null">{{ weightedLabel }}</em>
                </span>
                <span class="num">{{ closingDisplay == null ? '—' : (closingDisplay | number : '1.2-2') }}</span>
            </div>

            <div class="fx-hist-summary-row is-total" *ngIf="!isPreviousYear">
                <span>{{ deltaLabel }}</span>
                <span class="num">{{ delta | number : '1.2-2' }}</span>
                <span></span>
                <span></span>
            </div>
        </div>

        <!-- Not a <p>: the app's global `#global-modal-placeholder p` rule centres modal paragraphs. -->
        <div class="fx-hist-hint" *ngIf="isPreviousYearEditable">{{ rateHintLabel }}</div>
        <!-- Read-only because the prior year was carried over; the unlock sits above the table. -->
        <div class="fx-hist-blocked" *ngIf="showLockedNote">{{ lockedNoteLabel }}</div>

        <!--
            Read-only history of closed years, carried forward on autofill. It shows HOW the
            position was translated over time; it does NOT validate the prior-year rate above,
            which is entered (or carried) independently.
        -->
        <!-- Its own card: the history reads as one unit, and never as the heading of the movements below. -->
        <div class="fx-hist-archive" *ngIf="hasHistory" [class.is-open]="visibleHistoryYears.length > 0">
            <button
                type="button"
                class="fx-hist-archive-head"
                [disabled]="!showHistoryToggle"
                [attr.aria-expanded]="visibleHistoryYears.length > 0"
                (click)="toggleHistory()"
            >
                <span class="fx-hist-archive-title">{{ historyLabel }}</span>
                <span class="fx-hist-archive-count">{{ historyCountLabel }}</span>
                <span class="fx-hist-archive-toggle" *ngIf="showHistoryToggle">
                    {{ historyExpanded ? historyLessLabel : historyMoreLabel }}
                    <span class="fx-hist-chevron" aria-hidden="true"></span>
                </span>
            </button>

            <div class="fx-hist-archive-body" *ngIf="visibleHistoryYears.length > 0">
                <div class="fx-hist-history" *ngFor="let year of visibleHistoryYears; trackBy: trackByYear">
                    <div class="fx-hist-history-year">{{ yearLabel }} {{ formatArchiveYear(year) }}</div>
                    <div class="fx-hist-table">
                        <div class="fx-hist-table-head fx-hist-history-grid">
                            <span>{{ dateLabel }}</span>
                            <span>{{ descriptionLabel }}</span>
                            <span class="num">{{ functionalLabel }} ({{ functionalCurrencyName }})</span>
                            <span class="num">{{ rateLabel }}</span>
                            <span class="num">{{ displayLabel }} ({{ displayCurrencyName }})</span>
                        </div>

                        <!-- Opening and closing bracket the movements, so the year reconciles on screen. -->
                        <div class="fx-hist-history-grid fx-hist-history-row is-balance" *ngIf="year.balances as b">
                            <span></span>
                            <span>{{ historyOpeningLabel }}</span>
                            <span class="num">{{ b.openingFunctional | number : '1.2-2' }}</span>
                            <span class="num">{{
                                b.openingRate == null ? '' : (b.openingRate | number : '1.5-5')
                            }}</span>
                            <span class="num">{{ b.openingDisplay | number : '1.2-2' }}</span>
                        </div>

                        <div class="fx-hist-history-grid fx-hist-history-row" *ngFor="let mv of year.entry.movements">
                            <span>{{ mv.date == null ? '' : (mv.date | dateLocale) }}</span>
                            <span>{{ mv.description }}</span>
                            <span class="num">{{ mv.functionalAmount | number : '1.2-2' }}</span>
                            <span class="num">{{ mv.rate | number : '1.5-5' }}</span>
                            <span class="num">{{ displayOf(mv) | number : '1.2-2' }}</span>
                        </div>

                        <div
                            class="fx-hist-history-grid fx-hist-history-row is-balance is-closing"
                            *ngIf="year.balances as b"
                        >
                            <span></span>
                            <span>{{ historyClosingLabel }}</span>
                            <span class="num">{{ b.closingFunctional | number : '1.2-2' }}</span>
                            <span class="num">{{
                                b.closingRate == null ? '' : (b.closingRate | number : '1.5-5')
                            }}</span>
                            <span class="num">{{ b.closingDisplay | number : '1.2-2' }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>


        <!-- Movements can only build on a prior-year value that exists. -->
        <div class="fx-hist-blocked" *ngIf="!isPreviousYear && isPreviousYearMissing">{{ previousRequiredLabel }}</div>

        <ng-container *ngIf="!isPreviousYear && !isPreviousYearMissing">
            <div class="fx-hist-section">{{ movementsLabel }}</div>

            <div class="fx-hist-table">
                <div class="fx-hist-table-head">
                    <span>{{ dateLabel }}</span>
                    <span>{{ descriptionLabel }}</span>
                    <span class="num">{{ functionalLabel }} ({{ functionalCurrencyName }})</span>
                    <span class="num">{{ rateLabel }}</span>
                    <span class="num">{{ displayLabel }} ({{ displayCurrencyName }})</span>
                    <span></span>
                </div>

                <div class="fx-hist-table-row" *ngFor="let mv of movements; let i = index; trackBy: trackById">
                    <!-- Same date entry as the new-project wizard: type it in any separator style
                         (or as bare digits), or pick it from the calendar. -->
                    <div class="fx-hist-date-field">
                        <!-- ngModel, not [value]: hamInput is a ControlValueAccessor and only
                             repaints through writeValue(), so a raw [value] leaves the field stale
                             when the calendar fills it in. -->
                        <input
                            hamInput
                            #dateField
                            [suffixIcon]="'/assets/hamIcons/calendar-day-outline.svg'"
                            [hideRemoveXButton]="true"
                            [placeholder]="datePlaceholderLabel"
                            [ngModel]="mv.dateInput"
                            [ngModelOptions]="{ standalone: true }"
                            (ngModelChange)="onDateInput(i, $event)"
                            (blur)="onDateInputBlur(i)"
                            (keydown.enter)="onDateEnter($event, i)"
                            (keydown.escape)="onDateEscape($event)"
                            (click)="openDateFlyout(dateField, i)"
                        />
                        <span
                            class="menu-flyout"
                            hamFlyout
                            *ngIf="dateFlyoutIndex === i"
                            [position]="flyoutPosition"
                            (closeFlyout)="closeDateFlyout()"
                        >
                            <div class="custom-datepicker-wrapper">
                                <mat-calendar
                                    [selected]="calendarSelected"
                                    [startAt]="calendarSelected"
                                    (selectedChange)="onCalendarSelect(i, $event)"
                                ></mat-calendar>
                            </div>
                        </span>
                    </div>

                    <input
                        hamInput
                        [ngModel]="mv.description"
                        [ngModelOptions]="{ standalone: true }"
                        (ngModelChange)="onDescriptionChange(i, $event)"
                    />
                    <input
                        hamInput
                        type="number"
                        step="0.01"
                        [ngModel]="mv.functionalAmount"
                        [ngModelOptions]="{ standalone: true }"
                        (ngModelChange)="onFunctionalChange(i, $event)"
                    />
                    <input
                        hamInput
                        type="number"
                        step="0.00001"
                        [decimalPlaces]="rateDecimalPlaces"
                        [ngModel]="mv.rate"
                        [ngModelOptions]="{ standalone: true }"
                        (ngModelChange)="onRateChange(i, $event)"
                    />
                    <input
                        hamInput
                        type="number"
                        step="0.01"
                        [ngModel]="mv.displayAmount"
                        [ngModelOptions]="{ standalone: true }"
                        (ngModelChange)="onDisplayChange(i, $event)"
                        (blur)="onDisplayBlur(i)"
                    />
                    <button type="button" class="fx-hist-delete" [title]="deleteLabel" (click)="onRemoveMovement(i)">
                        <span class="ham-icon"><i class="trash-icon"></i></span>
                    </button>
                </div>
            </div>

            <button type="button" class="fx-hist-add" (click)="onAddMovement()">+ {{ addLabel }}</button>

            <div class="fx-hist-remainder" [class.fx-hist-remainder-open]="unexplained !== 0">
                <span>{{ explainedLabel }}</span>
                <span class="fx-hist-num">
                    {{ declared | number : '1.2-2' }} / {{ delta | number : '1.2-2' }}
                </span>
            </div>
        </ng-container>
    </div>
</ham-modal>
